import moment from "moment-timezone";

import Application from "shared/application";
import _ from "underscore";

const DateFormatter = { };

const dateRangeFunctions = {
  "today": function() {
    var today = this._today().toDate();
    return [today, today];
  },

  "yesterday": function() {
    var yesterday = this._today().subtract(1, 'days').toDate();
    return [yesterday, yesterday];
  },

  "7days": function() {
    return [
      this._today().subtract(7, 'days').toDate(),
      this._today().toDate()
    ];
  },

  "last_7days": function() {
    return [
      this._today().subtract(7, 'days').toDate(),
      this._today().subtract(1, 'days').toDate()
    ];
  },

  "30days": function() {
    return [
      this._today().subtract(30, 'days').toDate(),
      this._today().toDate()
    ];
  },

  "last_30days": function() {
    return [
      this._today().subtract(30, 'days').toDate(),
      this._today().subtract(1, 'days').toDate()
    ];
  },

  "month": function() {
    return [
      this._today().startOf('month').toDate(),
      this._today().toDate()
    ];
  },

  "quarter": function() {
    return [
      this._today().startOf('quarter').toDate(),
      this._today().toDate()
    ];
  },

  "year": function() {
    return [
      this._today().startOf('year').toDate(),
      this._today().toDate()
    ];
  },

  "last_month": function() {
    var previousMonthEnd = this._today().startOf('month').subtract(1, 'day');

    return [
      previousMonthEnd.clone().startOf('month').toDate(),
      previousMonthEnd.toDate()
    ];
  },

  "last_quarter": function() {
    var previousQuarterEnd = this._today().startOf('quarter').subtract(1, 'day');

    return [
      previousQuarterEnd.clone().startOf('quarter').toDate(),
      previousQuarterEnd.toDate()
    ];
  },

  _today: function() {
    var day = DateFormatter.inLocalTime(new Date(), { forceBrowserTimezone: true });
    return day.startOf('day');
  }
};

const previousDateRangeFunctions = {
  "today": function() {
    var today = dateRangeFunctions._today();
    var previousDay = today.subtract(1, 'days').toDate();
    return [previousDay, previousDay];
  },

  "yesterday": function() {
    var today = dateRangeFunctions._today();
    var previousDay = today.subtract(2, 'days').toDate();
    return [previousDay, previousDay];
  },

  "7days": function() { // 8 days total
    var previousEnd = dateRangeFunctions._today().subtract(8, 'days');

    return [
      previousEnd.clone().subtract(7, 'days').toDate(),
      previousEnd.toDate()
    ];
  },

  "last_7days": function() { // 7 days total
    var previousEnd = dateRangeFunctions._today().subtract(8, 'days');

    return [
      previousEnd.clone().subtract(6, 'days').toDate(),
      previousEnd.clone().toDate()
    ];
  },

  "30days": function() { // 31 days total
    var previousEnd = dateRangeFunctions._today().subtract(31, 'days');

    return [
      previousEnd.clone().subtract(30, 'days').toDate(),
      previousEnd.toDate()
    ];
  },

  "last_30days": function() { // 30 days total
    var previousEnd = dateRangeFunctions._today().subtract(31, 'days');

    return [
      previousEnd.clone().subtract(29, 'days').toDate(),
      previousEnd.toDate()
    ];
  },

  "month": function() {
    return this._rangeForPeriodThroughToday('month');
  },

  "quarter": function() {
    return this._rangeForPeriodThroughToday('quarter');
  },

  "year": function() {
    return this._rangeForPeriodThroughToday('year');
  },

  "last_month": function() {
    return this._rangeForLastPeriod('month');
  },

  "last_quarter": function() {
    return this._rangeForLastPeriod('quarter');
  },

  // period should be 'month', 'quarter', or 'year'
  //
  // returns a range from the start of the previous period,
  // up through the same day in that period as today is in the current period
  _rangeForPeriodThroughToday: function(period) {
    var today = dateRangeFunctions._today();
    var start = today.clone().startOf(period).subtract(1, period);

    return [
      start.toDate(),
      today.subtract(1, period).toDate()
    ];
  },

  // period should be 'month', 'quarter', or 'year'
  //
  // returns a range from the start of the period prior to the previous period,
  // up through the end of that period
  _rangeForLastPeriod: function(period) {
    var today = dateRangeFunctions._today();
    var start = today.clone().startOf(period).subtract(2, period);

    return [
      start.toDate(),
      start.clone().endOf(period).toDate()
    ];
  }
};

_.extend(DateFormatter, {
  timeDifferenceSeconds(seconds) {
    var days = Math.floor(seconds / (60 * 60 * 24));
    if (days >= 2) {
      seconds -= (60 * 60 * 24 * days);
    } else {
      days = 0;
    }
    var hours = Math.floor(seconds / (60 * 60));
    seconds -= (60 * 60 * hours);
    var minutes = Math.floor(seconds / 60);
    seconds -= (60 * minutes);

    var result = [];
    if (days > 0) {
      result.push(days);
      result.push('days');
    }
    if (hours > 0 || result.length > 0) {
      result.push(hours);
      result.push(hours === 1 ? 'hour' : 'hours');
    }
    if (minutes > 0 || result.length > 0) {
      result.push(minutes);
      result.push(minutes === 1 ? 'min' : 'mins');
    }
    if (seconds > 0 || result.length > 0) {
      result.push(seconds);
      result.push(seconds === 1 ? 'sec' : 'secs');
    }
    return result.join(' ');
  },

  secondsToString(rawSeconds) {
    var roundedTime = Math.round(rawSeconds);
    var hours = Math.floor(roundedTime / (60 * 60));
    var minutes = (Math.floor((roundedTime / 60) - (hours * 60))).toString();
    var seconds = (roundedTime % 60).toString();
    if (seconds.length === 1) {
      seconds = "0" + seconds;
    }
    if (hours === 0) {
      return minutes + ":" + seconds;
    } else {
      if (minutes.length === 1) {
        minutes = "0" + minutes;
      }
      return hours + ":" + minutes + ":" + seconds;
    }
  },

  fromDBWithDifference(val) {
    const timeDiffSeconds = Math.floor((new Date() - new Date(val)) / 1000);
    const isPast = timeDiffSeconds >= 0;
    const formattedDiff = this.timeDifferenceSeconds(Math.abs(timeDiffSeconds));

    return this.fromDB(val) + ' (' + (isPast ? (formattedDiff + ' ago') : ('in ' + formattedDiff)) + ')';
  },

  fromDB(val) {
    return this.fromDate(val, "date_and_time");
  },

  dateOnlyRenderer(val) {
    return this.fromDate(val, "date_only");
  },

  fromDate(date, outputFormat, forceBrowserTimezone) {
    if (!date || date === "") {
      return "-";
    } else {
      var localeFormat = this.localeFormatString(outputFormat);
      return this.inLocalTime(date, { forceBrowserTimezone: forceBrowserTimezone }).format(localeFormat);
    }
  },

  // DateRange methods currently operate in browser timezone, rather than user's logged in timezone, since these
  //  methods are used (and somewhat coupled) to the date_range_selector which uses Bootstrap DatePicker, which
  //  operates in browser time - and users expect to select dates in the UI based on their browser/system time
  fromDateRange(dateRange, dateFilter) {
    var parseFormat = 'date_range_selector';
    var outputFormat = 'date_range_selector_long';
    var joinString = ' - ';
    var singleDateFilters = ['today', 'yesterday'];
    var dateRangeString, formattedDates;

    formattedDates = dateRange.map(function(dateInput) {
      var date = (typeof dateInput === "string") ? this.parse(dateInput, parseFormat) : dateInput;
      return this.fromDate(date, outputFormat, true);
    }.bind(this));

    if (_.contains(singleDateFilters, dateFilter)) {
      dateRangeString = formattedDates[0];
    } else {
      dateRangeString = formattedDates.join(joinString);
    }

    return dateRangeString;
  },

  parse(date, parseFormat, timezone) {
    if (timezone) {
      // for date strings without a timezone, use the specified timezone (some UIs provide a TZ picker)
      return moment.tz(date, this.localeFormatString(parseFormat), timezone).toDate();
    } else {
      // parse generically, if a timezone exists in the date string, it will be used, otherwise the date will be based on local time
      return moment(date, this.localeFormatString(parseFormat)).toDate();
    }
  },

  inLocalTime(date, options) {
    options = options || {};

    if (!options.forceBrowserTimezone && Application.timezone) {
      // prefer to render in the logged in user's timezone
      return moment.tz(date, Application.timezone);
    } else {
      // fallback is to user the browser's timezone
      return moment(date);
    }
  },

  localeFormatString(format) {
    switch (Application.locale) {
    case "UK":
      switch (format) {
      // using momentJS format: https://momentjs.com/docs/#/displaying/format/
      case "date_range_selector":
      case "date_only":
        return "DD/MM/Y";
      case "date_range_selector_long":
        return "DD MMM, Y";
      case "date_and_time":
        return 'D/M/YY kk:mm';
      case "four_digit_year_date_and_time":
        return 'D/M/YYYY kk:mm';
      case "time_only":
        return "kk:mm";

        // Bootstrap datepicker format - https://bootstrap-datepicker.readthedocs.io/en/stable/options.html#format
      case "datepicker":
        return "dd/mm/yyyy";
      case "datepicker_as_param":
        return "Y-MM-DD";

        // jQuery timepicker format - https://github.com/jonthornton/jquery-timepicker#options
      case "timepicker":
        return "G:i";

      default:
        throw new Error("Unknown date format for " + Application.locale + ": " + format);
      }

    default:
      switch (format) {
      // using momentJS format: https://momentjs.com/docs/#/displaying/format/
      case "date_range_selector":
      case "date_only":
        return "MM/DD/Y";
      case "date_range_selector_long":
        return "MMM DD, Y";
      case "date_and_time":
        return 'M/D/YY h:mm a';
      case "four_digit_year_date_and_time":
        return 'M/D/YYYY h:mm a';
      case "time_only":
        return "h:mm a";

        // Bootstrap datepicker format - https://bootstrap-datepicker.readthedocs.io/en/stable/options.html#format
      case "datepicker":
        return "mm/dd/yyyy";
      case "datepicker_as_param":
        return "Y-MM-DD";

        // jQuery timepicker format - https://github.com/jonthornton/jquery-timepicker#options
      case "timepicker":
        return "g:i a";

      default:
        throw new Error("Unknown date format for " + Application.locale + ": " + format);
      }
    }
  },

  dateRange(dateSelector) {
    return dateRangeFunctions[dateSelector]();
  },

  previousDateRange(dateSelector) {
    return previousDateRangeFunctions[dateSelector]();
  },

  customPreviousDateRange(customRange) {
    var start = moment(customRange[0]);
    var end = moment(customRange[1]);
    var numDaysDiff = end.diff(start, 'days');
    var previousEnd = start.subtract(1, 'days');

    return [
      previousEnd.clone().subtract(numDaysDiff, 'days').toDate(),
      previousEnd.toDate()
    ];
  }
});

export default DateFormatter;
